import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Validators, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import {
  AssignmentCustomerResponse,
  baseAssignmentsActions,
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  BaseModalService,
  BaseOfferStatus,
  BaseSigningStatusEnum,
  BaseTabInterface,
  CustomerInquiryOfferListItemResponse,
  RequirementCategoryResponse,
  BaseDeregisterFormInterface,
  BaseDateService,
  InquiryResponse,
  BaseSigningStatusBySide,
  BaseContractSigningTypeEnum,
  ContractResponse,
  BaseSigningStatusType,
  BaseRolesService,
  BASE_PERMISSIONS,
} from 'base';
import { BaseCustomerOfferActions } from '../../_enums/base-customer-offer-actions.enum';
import {
  BaseCustomerOfferInitialTabList,
  BaseCustomerOfferInitialAdminTabList,
} from '../../_const/base-customer-offer-tab-list.const';
import { BaseCustomerOfferTabsEnum } from '../../_enums/base-customer-offer-tabs.enum';
import { BaseCustomerOfferActionsService } from '../../../../shared/_services/base-customer-offer-actions.service';

@Component({
  selector: 'base-customer-offer-info-modal',
  templateUrl: './base-customer-offer-info-modal.component.html',
})
export class BaseCustomerOfferInfoModalComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('deregisterCandidateModalBody') deregisterCandidateModalBody: ElementRef;
  @Input() offer: CustomerInquiryOfferListItemResponse & { order?: number };
  @Input() inquiry: InquiryResponse;
  @Input() inquiryRequirementCategories: RequirementCategoryResponse[] = [];
  @Input() assignment: AssignmentCustomerResponse;
  @Input() assignmentContractSigningStatus: BaseSigningStatusType;
  @Input() openOperationInfo: boolean;
  @Input() isAssignmentsAdmin: boolean;
  @Input() modalRef: NgbModalRef;
  @Output() acceptOrRejectOffer: EventEmitter<BaseCustomerOfferActions> = new EventEmitter<BaseCustomerOfferActions>();
  tabs: BaseTabInterface[] = [];
  currentTab: BaseTabInterface;
  isOfferConfirmed: boolean;
  isOfferOpen: boolean;
  isFullySigned: boolean;
  isSignedByPartner: boolean;
  isSignedByMyself: boolean;
  isNotSigned: boolean;
  isAssignmentDeRegistered = false;
  isEndedAssignment = false;
  deregisterFormSubmitted = false;
  statusButtonBorder: string;
  signButtonTitle: string;
  avatarSize = BaseAvatarSize;
  buttonSizes = BaseButtonSize;
  customerOfferActions = BaseCustomerOfferActions;
  offerStatuses = BaseOfferStatus;
  signingStatusBySide = BaseSigningStatusBySide;
  contractSigningType = BaseContractSigningTypeEnum;
  deregisterForm: FormGroup<BaseDeregisterFormInterface>;
  validContract: ContractResponse;
  customerOfferTabsEnum = BaseCustomerOfferTabsEnum;
  isAcceptButtonAvailable = false;
  isRejectButtonAvailable = false;

  constructor(
    private fb: NonNullableFormBuilder,
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private baseRolesService: BaseRolesService,
    private offerActionsService: BaseCustomerOfferActionsService
  ) {}

  get showWorkCouncilTab() {
    return (
      this.baseRolesService.hasWorkCouncilRole() ||
      this.baseRolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_WORK_COUNCIL_COMMUNICATION)
    );
  }

  ngOnInit(): void {
    this.isOfferConfirmed = this.offer.status === this.offerStatuses.confirmed;
    this.isOfferOpen = this.offer.status === this.offerStatuses.open;
    this.setTabs();

    this.setAssignmentEnded();
    this.setAssignmentSignStatus();
    this.statusButtonBorder = this.setSignButtonClass();
    this.signButtonTitle = this.setSignButtonTitle();

    const locationWorkCouncilSettings = {
      workCouncilActivated: !!this.inquiry.customerLocation?.workCouncilActivated,
      acceptOfferAfterPreselection: !!this.inquiry.customerLocation?.acceptOfferAfterPreselection,
      acceptOfferAfterWorkCouncilApproval: !!this.inquiry.customerLocation?.acceptOfferAfterWorkCouncilApproval,
    };
    this.isAcceptButtonAvailable = this.offerActionsService.checkAcceptButtonAvailability(
      this.offer,
      locationWorkCouncilSettings
    );
    this.isRejectButtonAvailable = this.offerActionsService.checkRejectButtonAvailability(this.offer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.assignment) {
      this.validContract = this.assignment?.contracts?.find((contract) => contract.valid) as ContractResponse;
      this.setAssignmentEnded();
      this.setAssignmentSignStatus();
      this.statusButtonBorder = this.setSignButtonClass();
      this.signButtonTitle = this.setSignButtonTitle();
    }
  }

  ngOnDestroy() {
    this.store.dispatch(baseAssignmentsActions.clearAssignment());
    this.acceptOrRejectOffer.emit(this.customerOfferActions.default);
    this.store.dispatch(baseAssignmentsActions.clearWorkCouncilCommentList());
  }

  onTabChange(event: BaseTabInterface): void {
    this.currentTab = event;
  }

  closeModal(): void {
    this.modalRef.close();
  }

  onAcceptOrRejectOffer(action: BaseCustomerOfferActions): void {
    this.acceptOrRejectOffer.emit(action);
    this.closeModal();
  }

  openCandidateDeregisterModal(): void {
    if (!this.deregisterForm) {
      this.deregisterForm = this.fb.group({
        cancelationDate: ['', [Validators.required]],
        note: [''],
      });
    }

    const deregisterModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.unsubscribe',
        bodyTemplate: this.deregisterCandidateModalBody,
        okText: 'action.yesSave',
        cancelText: 'action.cancel',
        canBeClosed: () => {
          this.deregisterFormSubmitted = true;
          return this.deregisterForm.valid;
        },
      },
      {
        size: 'lg',
      }
    );

    deregisterModalRef.result.then(() => {
      this.store.dispatch(
        baseAssignmentsActions.assignmentCustomerCancellation({
          payload: { assignment: this.assignment.id, body: this.deregisterForm.getRawValue() },
        })
      );
      this.isAssignmentDeRegistered = true;
    }, noop);
  }

  private setTabs(): void {
    this.tabs =
      this.isOfferConfirmed && this.isAssignmentsAdmin
        ? [...BaseCustomerOfferInitialAdminTabList]
        : [...BaseCustomerOfferInitialTabList];

    if (!this.offer.anonymous || this.isOfferConfirmed) {
      this.tabs.splice(2, 0, {
        id: BaseCustomerOfferTabsEnum.Reviews,
        title: 'ratings',
      });
    }

    if (!this.showWorkCouncilTab || !this.offer.id) {
      this.tabs = this.tabs.filter((tab) => tab.id !== BaseCustomerOfferTabsEnum.WorkCouncil);
    }

    this.currentTab = this.tabs[BaseCustomerOfferTabsEnum.MaterData];
  }

  private setAssignmentEnded(): void {
    if (this.assignment) {
      this.isEndedAssignment = BaseDateService.isAfter(new Date(), new Date(this.assignment.endDate));
    }
  }

  private setAssignmentSignStatus(): void {
    this.isFullySigned = this.assignmentContractSigningStatus === BaseSigningStatusEnum.SIGNED;
    this.isSignedByPartner = this.assignmentContractSigningStatus === BaseSigningStatusEnum.SIGNED_BY_PSP;
    this.isSignedByMyself = this.assignmentContractSigningStatus === BaseSigningStatusEnum.SIGNED_BY_CUSTOMER;
    this.isNotSigned = this.assignmentContractSigningStatus === BaseSigningStatusEnum.UNSIGNED;
  }

  private setSignButtonTitle(): string {
    return this.isFullySigned
      ? 'candidates.status.signed'
      : this.isSignedByMyself
      ? 'externalEmployees.contractStatus.waitingForPartner'
      : this.isNotSigned || this.isSignedByPartner
      ? 'externalEmployees.contractStatus.signatureRequired'
      : '';
  }

  private setSignButtonClass(): string {
    return this.isFullySigned
      ? 'state-btn-border-green no-pointer'
      : this.isSignedByMyself
      ? 'state-btn-border-yellow no-pointer'
      : this.isNotSigned || this.isSignedByPartner
      ? 'state-btn-border-red no-pointer'
      : '';
  }
}
